import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Topbar } from "@/components/parcelio/topbar";
import { NotificationDetailDialog } from "@/components/parcelio/notification-detail-dialog";
import addVehicleImg from "@/assets/dashboard-add-vehicle.jpg";
import trackNowImg from "@/assets/dashboard-track-now.jpg";
import { StatusPill } from "@/components/parcelio/status-pill";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import {
  Package,
  TrendingUp,
  CheckCircle2,
  Users,
  Truck,
  Wallet as WalletIcon,
  AlertTriangle,
  ArrowUpRight,
  Plus,
  Navigation,
  Search,
  PackagePlus,
} from "lucide-react";
import {
  analyticsApi,
  notificationsApi,
  parcelsApi,
  directoryApi,
  vehiclesApi,
  walletApi,
  usersApi,
  ApiError,
  type AnalyticsSummary,
  type Notification,
  type Parcel,
  type DriverDirectoryEntry,
  type Vehicle,
  type Wallet,
  type CorporateAccount,
  type UserProfile,
} from "@/lib/api";
import { formatParcelValue, toLegacyStatus } from "@/lib/parcel-adapters";
import { formatNaira } from "@/lib/mock-data";
import { cn, timeAgo } from "@/lib/utils";
import { useAuth } from "@/stores/auth";
import { LoadingBlock } from "@/components/ui/spinner";

export default function Dashboard() {
  const session = useAuth((s) => s.session);
  const kind = session?.kind;
  const token = session?.accessToken;

  if (kind === "corporate") return <CorporateHome />;
  if (kind === "personal") return <PersonalHome />;
  if (kind === "client") return <ClientHome />;
  return <StaffHome token={token} />;
}

const KIND_LABEL: Record<string, string> = {
  staff: "Staff",
  corporate: "Corporate account",
  personal: "Personal account",
  client: "Client account",
};

function useProfile() {
  const session = useAuth((s) => s.session);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    usersApi.get(session.userId, session.accessToken).then((p) => {
      if (!cancelled) setProfile(p);
    }).catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [session]);
  return profile;
}

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good morning";
  if (h < 17) return "Good afternoon";
  return "Good evening";
}

function initialsOf(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase() || "U";
}

/** Personalized greeting banner — first thing on every dashboard variant. */
function Hero({ profile }: { profile: UserProfile | null }) {
  const kind = useAuth((s) => s.session?.kind);
  const firstName = profile?.full_name?.split(" ")[0];
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary via-primary to-blue-900 p-6 text-primary-foreground shadow-lg sm:p-8">
      <div className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-24 -left-24 h-64 w-64 rounded-full bg-success/20 blur-3xl" />
      <div
        className="pointer-events-none absolute inset-0 opacity-10"
        style={{ backgroundImage: "radial-gradient(circle, rgba(255,255,255,0.6) 1px, transparent 1px)", backgroundSize: "18px 18px" }}
      />
      <div className="relative z-10 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl border border-white/20 bg-white/15 text-xl font-bold tracking-wide backdrop-blur-sm sm:h-16 sm:w-16 sm:text-2xl">
            {profile ? initialsOf(profile.full_name) : "…"}
          </div>
          <div>
            <p className="text-xs font-medium uppercase tracking-wider text-white/80 sm:text-sm">{greeting()}</p>
            <h1 className="text-2xl font-bold tracking-tight sm:text-3xl">{firstName ? `${firstName} 👋` : "Welcome!"}</h1>
            <div className="mt-2 inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-3 py-0.5 text-xs font-medium backdrop-blur-sm">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-300" />
              {kind ? KIND_LABEL[kind] ?? kind : "Account"}
            </div>
          </div>
        </div>
        <div className="hidden text-right text-sm text-white/80 sm:block">
          <p className="font-medium">{today}</p>
          <p className="text-xs text-white/60">Parcelio</p>
        </div>
      </div>
    </div>
  );
}

// ---- shared bits -----------------------------------------------------------

/** Big tappable action tile — gradient "photo" background (no real imagery
 * in this app yet) with a color overlay and a giant watermark icon, in the
 * style of a photo-card CTA. */
function ActionCard({
  to,
  onClick,
  title,
  subtitle,
  icon: Icon,
  tone,
  image,
  disabled,
}: {
  to?: string;
  onClick?: () => void;
  title: string;
  subtitle: string;
  icon: typeof Package;
  tone: "primary" | "success";
  image: string;
  disabled?: boolean;
}) {
  // Translucent tint over the photo (not an opaque fill) — the color
  // overlay stays, the background image shows through it.
  const toneClasses = tone === "primary"
    ? "from-primary/75 via-primary/60 to-blue-900/80"
    : "from-success/75 via-success/60 to-emerald-900/80";

  const content = (
    <div
      className={cn(
        "group relative isolate flex h-32 items-center overflow-hidden rounded-2xl p-5 text-left text-primary-foreground shadow-md transition-all duration-300",
        disabled ? "pointer-events-none opacity-60" : "hover:-translate-y-1 hover:shadow-xl"
      )}
    >
      <img
        src={image}
        alt=""
        aria-hidden="true"
        className="absolute inset-0 h-full w-full object-cover transition-transform duration-500 group-hover:scale-110"
      />
      <div className={cn("absolute inset-0 bg-gradient-to-br", toneClasses)} />
      <div className="absolute inset-0 bg-black/10" />
      <div className="relative z-10 min-w-0">
        <div className="flex h-9 w-9 items-center justify-center rounded-lg border border-white/20 bg-white/15 backdrop-blur-sm">
          <Icon className="h-4 w-4" />
        </div>
        <div className="mt-3 text-base font-semibold">{title}</div>
        <div className="text-xs text-white/80">{subtitle}</div>
      </div>
      <ArrowUpRight className="relative z-10 ml-auto h-5 w-5 shrink-0 text-white/70 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
    </div>
  );

  if (disabled) return content;
  if (onClick) return <button type="button" onClick={onClick} className="block w-full">{content}</button>;
  return <Link to={to ?? "#"}>{content}</Link>;
}

/** Vehicle picker popup for the "Live track" dashboard tile — staff/
 * corporate/personal accounts can have more than one vehicle, so the tile
 * opens this instead of linking straight to a single track page. */
function LiveTrackDialog({ open, onOpenChange, vehicles }: { open: boolean; onOpenChange: (v: boolean) => void; vehicles: Vehicle[] }) {
  const [q, setQ] = useState("");
  const filtered = vehicles.filter((v) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return v.plate.toLowerCase().includes(s) || (v.make ?? "").toLowerCase().includes(s) || (v.model ?? "").toLowerCase().includes(s);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Navigation className="h-4 w-4" /> Live track</DialogTitle>
          <DialogDescription>Pick a vehicle to open its live map.</DialogDescription>
        </DialogHeader>
        {vehicles.length > 3 ? (
          <div className="relative">
            <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search plate or model…" className="pl-8" />
          </div>
        ) : null}
        <div className="max-h-80 overflow-y-auto">
          {filtered.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted-foreground">
              {vehicles.length === 0 ? "No vehicles yet." : "No vehicle matches."}
            </p>
          ) : (
            <ul className="divide-y">
              {filtered.map((v) => (
                <li key={v.id} className="flex items-center justify-between gap-3 py-3">
                  <div className="min-w-0">
                    <div className="truncate text-sm font-medium">{[v.make, v.model].filter(Boolean).join(" ") || v.plate}</div>
                    <div className="font-mono text-xs text-muted-foreground">{v.plate}</div>
                  </div>
                  {v.device_id ? (
                    <Button size="sm" asChild onClick={() => onOpenChange(false)}>
                      <Link to={`/app/vehicles/${v.id}/track`}><Navigation className="mr-1.5 h-3.5 w-3.5" /> Live track</Link>
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" disabled title="No tracker linked">No tracker</Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function KpiCard({
  label,
  value,
  icon: Icon,
  hint,
  tone,
}: {
  label: string;
  value: string | number;
  icon: typeof Package;
  hint?: string;
  tone: "info" | "success" | "warning" | "muted";
}) {
  return (
    <div className="surface-card min-w-0 p-3 sm:p-5">
      <div className="flex min-w-0 items-center justify-between gap-1">
        <span className="min-w-0 truncate text-[10px] font-medium uppercase tracking-wider text-muted-foreground sm:text-xs" title={label}>
          {label}
        </span>
        <div
          className={cn(
            "flex h-6 w-6 shrink-0 items-center justify-center rounded-lg sm:h-8 sm:w-8",
            tone === "info" && "bg-info/10 text-info",
            tone === "success" && "bg-success/10 text-success",
            tone === "warning" && "bg-warning/15 text-warning-foreground",
            tone === "muted" && "bg-muted text-muted-foreground"
          )}
        >
          <Icon className="h-3 w-3 sm:h-4 sm:w-4" />
        </div>
      </div>
      <div className="mt-2 truncate text-xl font-bold tracking-tight sm:mt-3 sm:text-3xl">{value}</div>
      {hint ? <div className="mt-1 truncate text-xs text-muted-foreground">{hint}</div> : null}
    </div>
  );
}

function DeliveriesCard({ title, parcels, viewAllTo }: { title: string; parcels: Parcel[]; viewAllTo: string }) {
  return (
    <div className="surface-card min-w-0 lg:col-span-2">
      <div className="flex min-w-0 items-center justify-between gap-2 border-b px-5 py-4">
        <h3 className="truncate text-sm font-semibold">{title}</h3>
        <Button variant="ghost" size="sm" className="shrink-0" asChild>
          <Link to={viewAllTo}>View all <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
        </Button>
      </div>
      <ul className="divide-y">
        {parcels.map((p, i) => (
          <li key={p.id} className={i >= 3 ? "hidden sm:block" : undefined}>
            <Link to={`/app/tracking/${p.id}`} className="grid grid-cols-[1fr,auto] items-center gap-3 px-5 py-4 hover:bg-muted/50">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-mono text-xs font-semibold text-muted-foreground">{p.tracking_id}</span>
                  <StatusPill status={toLegacyStatus(p.status)} />
                </div>
                <div className="mt-1 truncate text-sm font-medium">{p.receiver_name ?? "—"}</div>
                <div className="truncate text-xs text-muted-foreground">{p.dropoff_addr ?? "—"}</div>
              </div>
              <div className="text-right">
                <div className="text-sm font-medium">{formatParcelValue(p.amount_kobo)}</div>
                <div className="text-xs text-muted-foreground">{p.created_at ? timeAgo(p.created_at) : "—"}</div>
              </div>
            </Link>
          </li>
        ))}
        {parcels.length === 0 ? <li className="px-5 py-8 text-center text-sm text-muted-foreground">No deliveries yet.</li> : null}
      </ul>
      {parcels.length > 3 ? (
        <div className="border-t p-3 text-center sm:hidden">
          <Button variant="ghost" size="sm" asChild>
            <Link to={viewAllTo}>View more <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function AlertsCard({ alerts, onMarkRead }: { alerts: Notification[]; onMarkRead?: (id: string) => void }) {
  const token = useAuth((s) => s.session?.accessToken);
  const [selected, setSelected] = useState<Notification | null>(null);

  const markRead = async (id: string) => {
    setSelected((s) => (s && s.id === id ? { ...s, read_at: new Date().toISOString() } : s));
    onMarkRead?.(id);
    try {
      await notificationsApi.markRead(id, token);
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Could not mark notification as read");
    }
  };

  return (
    <div className="surface-card min-w-0 p-5">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <AlertTriangle className="h-4 w-4 shrink-0 text-destructive" />
          <h3 className="truncate text-sm font-semibold">Alerts & activity</h3>
        </div>
        <span className="shrink-0 rounded-full bg-destructive/10 px-2 py-0.5 text-xs font-medium text-destructive">{alerts.length}</span>
      </div>
      <ul className="mt-3 space-y-3">
        {alerts.map((a, i) => (
          <li
            key={a.id}
            onClick={() => setSelected(a)}
            className={cn("flex cursor-pointer items-start gap-3 rounded-lg border p-3", i >= 3 && "hidden sm:flex")}
          >
            <span
              className={cn(
                "mt-1 h-2 w-2 shrink-0 rounded-full",
                a.severity === "critical" && "bg-destructive",
                a.severity === "warning" && "bg-warning",
                a.severity === "info" && "bg-info"
              )}
            />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{a.title}</div>
              <div className="line-clamp-2 text-xs text-muted-foreground">{a.detail}</div>
            </div>
            <span className="shrink-0 text-xs text-muted-foreground">{timeAgo(a.created_at)}</span>
          </li>
        ))}
        {alerts.length === 0 ? <li className="py-6 text-center text-sm text-muted-foreground">Nothing yet.</li> : null}
      </ul>
      {alerts.length > 3 ? (
        <div className="mt-3 text-center sm:hidden">
          <Button variant="ghost" size="sm" asChild>
            <Link to="/app/notifications">View more <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
          </Button>
        </div>
      ) : null}
      <NotificationDetailDialog
        notification={selected}
        onOpenChange={(open) => !open && setSelected(null)}
        onMarkRead={markRead}
      />
    </div>
  );
}

function WalletCard({ wallet, spendKobo }: { wallet: Wallet | null; spendKobo: number }) {
  return (
    <div className="surface-card min-w-0 p-5 lg:col-span-2">
      <div className="flex min-w-0 items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          <WalletIcon className="h-4 w-4 shrink-0 text-primary" />
          <h3 className="truncate text-sm font-semibold">Wallet overview</h3>
        </div>
        <Button variant="ghost" size="sm" className="shrink-0" asChild>
          <Link to="/app/wallet">Manage <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
        </Button>
      </div>
      <div className="mt-4 grid min-w-0 gap-4 sm:grid-cols-2">
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">Available balance</div>
          <div className="mt-1 truncate text-3xl font-bold tracking-tight">{wallet ? formatNaira(wallet.available_kobo / 100) : "—"}</div>
        </div>
        <div className="min-w-0">
          <div className="text-xs text-muted-foreground">Today's spend</div>
          <div className="mt-1 truncate text-3xl font-bold tracking-tight">{formatNaira(spendKobo / 100)}</div>
        </div>
      </div>
    </div>
  );
}

// ---- staff (default: superadmin/admin/dispatch_officer/support) ------------

function StaffHome({ token }: { token?: string }) {
  const profile = useProfile();
  const roles = useAuth((s) => s.session?.roles) ?? [];
  const isDriverOnly = roles.length > 0 && roles.every((r) => r === "driver");
  const [summary, setSummary] = useState<AnalyticsSummary | null>(null);
  const [alerts, setAlerts] = useState<Notification[]>([]);
  const [activeParcels, setActiveParcels] = useState<Parcel[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [trackOpen, setTrackOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Analytics summary + full driver directory are staff-only server-side
    // (denied outright for driver-only logins) — skip rather than let a 403
    // blow up the dashboard for a driver.
    const emptySummary: AnalyticsSummary = { active_deliveries: 0, completed_today: 0, on_time_rate: 0, active_drivers: 0, revenue_today_kobo: 0, exceptions: 0 };
    Promise.all([
      isDriverOnly ? Promise.resolve(emptySummary) : analyticsApi.summary(token).catch((err) => { toast.error(err instanceof ApiError ? err.message : "Could not load dashboard summary"); return emptySummary; }),
      notificationsApi.list(undefined, token).then((list) => setAlerts(list.filter((n) => !n.read_at).slice(0, 5))).catch(() => {}),
      parcelsApi.list(undefined, token).then((list) => setActiveParcels(list.filter((p) => p.status !== "delivered" && p.status !== "failed").slice(0, 5))).catch(() => {}),
      isDriverOnly ? Promise.resolve([]) : directoryApi.drivers(token).then((list) => setDrivers(list.slice(0, 5))).catch(() => {}),
      vehiclesApi.list(token).then(setVehicles).catch(() => {}),
    ]).then(([s]) => setSummary(s as AnalyticsSummary)).finally(() => setLoading(false));
  }, [token, isDriverOnly]);

  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle="Operations overview"
        actions={
          <Button size="sm" className="gap-1.5" asChild>
            <Link to="/app/deliveries"><Plus className="h-4 w-4" /> New delivery</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        <Hero profile={profile} />
        {loading || !summary ? <LoadingBlock label="Loading dashboard…" /> : <>
          <section className="grid grid-cols-2 gap-4">
            <ActionCard
              onClick={() => setTrackOpen(true)}
              title="Live track"
              subtitle="See any vehicle's live location"
              icon={Navigation}
              tone="success"
              image={trackNowImg}
            />
            <ActionCard
              to="/app/deliveries"
              title="Add parcel"
              subtitle="Create a new delivery"
              icon={PackagePlus}
              tone="primary"
              image={addVehicleImg}
            />
          </section>
          <LiveTrackDialog open={trackOpen} onOpenChange={setTrackOpen} vehicles={vehicles} />

          <section className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
            <KpiCard label="Active deliveries" value={summary.active_deliveries} icon={Package} tone="info" />
            <KpiCard label="Completed today" value={summary.completed_today} icon={CheckCircle2} tone="success" />
            <KpiCard label="On-time rate" value={`${summary.on_time_rate}%`} icon={TrendingUp} tone="success" />
            <KpiCard label="Drivers online" value={summary.active_drivers} icon={Users} tone="muted" />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <WalletCard wallet={null} spendKobo={summary.revenue_today_kobo} />
            <AlertsCard alerts={alerts} onMarkRead={(id) => setAlerts((a) => a.filter((n) => n.id !== id))} />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <DeliveriesCard title="Active deliveries" parcels={activeParcels} viewAllTo="/app/deliveries" />
            {isDriverOnly ? null : (
              <div className="surface-card">
                <div className="flex items-center justify-between border-b px-5 py-4">
                  <h3 className="text-sm font-semibold">Driver availability</h3>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/app/users/drivers">Manage <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
                  </Button>
                </div>
                <ul className="divide-y">
                  {drivers.map((d, i) => (
                    <li key={d.id} className={cn("flex items-center gap-3 px-5 py-3", i >= 3 && "hidden sm:flex")}>
                      <div className="min-w-0 flex-1">
                        <div className="truncate text-sm font-medium">{d.name}</div>
                        <div className="truncate text-xs text-muted-foreground">{d.vehicle_plate ?? "No vehicle"}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs font-medium">{d.active_parcels} active</div>
                        <div className="text-xs text-muted-foreground">{d.completed_today} today</div>
                      </div>
                    </li>
                  ))}
                  {drivers.length === 0 ? <li className="px-5 py-8 text-center text-sm text-muted-foreground">No drivers yet.</li> : null}
                </ul>
                {drivers.length > 3 ? (
                  <div className="border-t p-3 text-center sm:hidden">
                    <Button variant="ghost" size="sm" asChild>
                      <Link to="/app/users/drivers">View more <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
                    </Button>
                  </div>
                ) : null}
              </div>
            )}
          </section>
        </>}
      </div>
    </>
  );
}

// ---- corporate ---------------------------------------------------------

function CorporateHome() {
  const session = useAuth((s) => s.session);
  const token = session?.accessToken;
  const profile = useProfile();
  const [corp, setCorp] = useState<CorporateAccount | null>(null);
  const [deliveries, setDeliveries] = useState<Parcel[]>([]);
  const [drivers, setDrivers] = useState<DriverDirectoryEntry[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [fleet, setFleet] = useState<Vehicle[]>([]);
  const [trackOpen, setTrackOpen] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    (async () => {
      try {
        // /corporates (full directory) is staff-only server-side — this
        // resolves the caller's own company server-side instead, which
        // actually works for a real corporate login.
        const mine = await directoryApi.myCorporate(token).catch(() => null);
        if (cancelled) return;
        setCorp(mine);

        const [allDrivers, deliveriesRes, w, feed, allVehicles] = await Promise.all([
          directoryApi.drivers(token),
          parcelsApi.list({ clientId: session.userId, corporateAccountId: mine?.id }, token),
          walletApi.get(session.userId, token).catch(() => null),
          notificationsApi.list(session.userId, token).catch(() => []),
          vehiclesApi.list(token).catch(() => []),
        ]);
        if (cancelled) return;
        setDrivers(mine ? allDrivers.filter((d) => d.corporate_account_id === mine.id).slice(0, 5) : []);
        setDeliveries(deliveriesRes.slice(0, 5));
        setWallet(w);
        setActivity(feed.slice(0, 5));
        setFleet(mine ? allVehicles.filter((v) => v.corporate_account_id === mine.id) : allVehicles);
      } catch {
        if (!cancelled) toast.error("Could not load dashboard");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [session, token]);

  const activeCount = deliveries.filter((p) => p.status !== "delivered" && p.status !== "failed").length;

  return (
    <>
      <Topbar
        title={corp ? corp.company : "Dashboard"}
        subtitle="Your deliveries, fleet & wallet"
        actions={
          <Button size="sm" className="gap-1.5" asChild>
            <Link to="/app/deliveries"><Plus className="h-4 w-4" /> New delivery</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        <Hero profile={profile} />
        {loading ? <LoadingBlock label="Loading dashboard…" /> : <>
          <section className="grid grid-cols-2 gap-4">
            <ActionCard
              onClick={() => setTrackOpen(true)}
              title="Live track"
              subtitle="See any fleet vehicle's live location"
              icon={Navigation}
              tone="success"
              image={trackNowImg}
            />
            <ActionCard
              to="/app/deliveries"
              title="Add parcel"
              subtitle="Create a new delivery"
              icon={PackagePlus}
              tone="primary"
              image={addVehicleImg}
            />
          </section>
          <LiveTrackDialog open={trackOpen} onOpenChange={setTrackOpen} vehicles={fleet} />

          <section className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
            <KpiCard label="Active deliveries" value={activeCount} icon={Package} tone="info" />
            <KpiCard label="Drivers" value={drivers.length} icon={Users} tone="muted" />
            <KpiCard label="Team members" value={corp?.members.length ?? 0} icon={Users} tone="muted" />
            <KpiCard label="Wallet balance" value={wallet ? formatNaira(wallet.available_kobo / 100) : "—"} icon={WalletIcon} tone="success" />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <WalletCard wallet={wallet} spendKobo={0} />
            <AlertsCard
              alerts={activity}
              onMarkRead={(id) =>
                setActivity((a) => a.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)))
              }
            />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <DeliveriesCard title="Recent deliveries" parcels={deliveries} viewAllTo="/app/deliveries" />
            <div className="surface-card">
              <div className="flex items-center justify-between border-b px-5 py-4">
                <h3 className="text-sm font-semibold">Drivers</h3>
                {corp ? (
                  <Button variant="ghost" size="sm" asChild>
                    <Link to={`/app/users/corporates/${corp.id}`}>Dashboard <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
                  </Button>
                ) : null}
              </div>
              <ul className="divide-y">
                {drivers.map((d) => (
                  <li key={d.id} className="flex items-center gap-3 px-5 py-3">
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">{d.name}</div>
                      <div className="truncate text-xs text-muted-foreground">{d.vehicle_plate ?? "No vehicle"}</div>
                    </div>
                    <div className="text-xs font-medium capitalize">{d.status.replace("_", " ")}</div>
                  </li>
                ))}
                {drivers.length === 0 ? <li className="px-5 py-8 text-center text-sm text-muted-foreground">No drivers in your fleet.</li> : null}
              </ul>
            </div>
          </section>
        </>}
      </div>
    </>
  );
}

// ---- personal (self-service vehicle owner) ----------------------------

function PersonalHome() {
  const session = useAuth((s) => s.session);
  const token = session?.accessToken;
  const profile = useProfile();
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [deliveries, setDeliveries] = useState<Parcel[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    Promise.all([
      vehiclesApi.list(token).then((list) => list.filter((v) => v.owner_id === session.userId)).catch(() => []),
      parcelsApi.list({ clientId: session.userId }, token).catch(() => []),
      walletApi.get(session.userId, token).catch(() => null),
      notificationsApi.list(session.userId, token).catch(() => []),
    ]).then(([v, d, w, feed]) => {
      if (cancelled) return;
      setVehicles(v);
      setDeliveries(d.slice(0, 5));
      setWallet(w);
      setActivity(feed.slice(0, 5));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [session, token]);

  const activeCount = deliveries.filter((p) => p.status !== "delivered" && p.status !== "failed").length;
  const [trackOpen, setTrackOpen] = useState(false);

  return (
    <>
      <Topbar
        title="My dashboard"
        subtitle="Your vehicles & deliveries"
        actions={
          <Button size="sm" className="gap-1.5" asChild>
            <Link to="/app/deliveries"><Plus className="h-4 w-4" /> New delivery</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        <Hero profile={profile} />
        {loading ? <LoadingBlock label="Loading dashboard…" /> : <>
          <section className="grid grid-cols-2 gap-4 sm:grid-cols-3">
            <ActionCard
              to="/app/vehicles?new=1"
              title="Add a vehicle"
              subtitle="Register a new vehicle to your fleet"
              icon={Truck}
              tone="primary"
              image={addVehicleImg}
            />
            <ActionCard
              onClick={() => setTrackOpen(true)}
              title="Live track"
              subtitle={vehicles.length > 0 ? "Live location & GPS history" : "Add a vehicle first to start tracking"}
              icon={Navigation}
              tone="success"
              image={trackNowImg}
            />
            <ActionCard
              to="/app/deliveries"
              title="Add parcel"
              subtitle="Create a new delivery"
              icon={PackagePlus}
              tone="primary"
              image={addVehicleImg}
            />
          </section>
          <LiveTrackDialog open={trackOpen} onOpenChange={setTrackOpen} vehicles={vehicles} />

          <section className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
            <KpiCard label="My vehicles" value={vehicles.length} icon={Truck} tone="info" />
            <KpiCard label="Active deliveries" value={activeCount} icon={Package} tone="info" />
            <KpiCard label="Wallet balance" value={wallet ? formatNaira(wallet.available_kobo / 100) : "—"} icon={WalletIcon} tone="success" />
            <KpiCard label="Total deliveries" value={deliveries.length} icon={CheckCircle2} tone="muted" />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <DeliveriesCard title="Recent deliveries" parcels={deliveries} viewAllTo="/app/deliveries" />
            <AlertsCard
              alerts={activity}
              onMarkRead={(id) =>
                setActivity((a) => a.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)))
              }
            />
          </section>

          <section className="surface-card">
            <div className="flex items-center justify-between border-b px-5 py-4">
              <h3 className="text-sm font-semibold">My vehicles</h3>
              <Button variant="ghost" size="sm" asChild>
                <Link to="/app/vehicles">Manage <ArrowUpRight className="ml-1 h-3.5 w-3.5" /></Link>
              </Button>
            </div>
            <ul className="divide-y">
              {vehicles.map((v) => (
                <li key={v.id} className="flex items-center justify-between px-5 py-3">
                  <div>
                    <div className="text-sm font-medium">{[v.make, v.model].filter(Boolean).join(" ") || v.plate}</div>
                    <div className="font-mono text-xs text-muted-foreground">{v.plate}</div>
                  </div>
                  <span className="text-xs font-medium capitalize">{v.status}</span>
                </li>
              ))}
              {vehicles.length === 0 ? <li className="px-5 py-8 text-center text-sm text-muted-foreground">No vehicles registered.</li> : null}
            </ul>
          </section>
        </>}
      </div>
    </>
  );
}

// ---- client (retail shipper) --------------------------------------------

function ClientHome() {
  const session = useAuth((s) => s.session);
  const token = session?.accessToken;
  const profile = useProfile();
  const [deliveries, setDeliveries] = useState<Parcel[]>([]);
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [activity, setActivity] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    Promise.all([
      parcelsApi.list({ clientId: session.userId }, token).catch(() => []),
      walletApi.get(session.userId, token).catch(() => null),
      notificationsApi.list(session.userId, token).catch(() => []),
    ]).then(([d, w, feed]) => {
      if (cancelled) return;
      setDeliveries(d);
      setWallet(w);
      setActivity(feed.slice(0, 5));
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [session, token]);

  const activeCount = deliveries.filter((p) => p.status !== "delivered" && p.status !== "failed").length;
  const totalSpentKobo = deliveries.reduce((sum, p) => sum + p.amount_kobo, 0);

  return (
    <>
      <Topbar
        title="Dashboard"
        subtitle="Your shipments & wallet"
        actions={
          <Button size="sm" className="gap-1.5" asChild>
            <Link to="/app/deliveries"><Plus className="h-4 w-4" /> New delivery</Link>
          </Button>
        }
      />
      <div className="flex-1 space-y-6 p-4 md:p-6">
        <Hero profile={profile} />
        {loading ? <LoadingBlock label="Loading dashboard…" /> : <>
          <section className="grid gap-4 sm:grid-cols-1">
            <ActionCard
              to="/app/deliveries"
              title="Add parcel"
              subtitle="Create a new delivery"
              icon={PackagePlus}
              tone="primary"
              image={addVehicleImg}
            />
          </section>

          <section className="grid min-w-0 grid-cols-2 gap-2 sm:grid-cols-4 sm:gap-4">
            <KpiCard label="Active deliveries" value={activeCount} icon={Package} tone="info" />
            <KpiCard label="Total shipments" value={deliveries.length} icon={CheckCircle2} tone="muted" />
            <KpiCard label="Wallet balance" value={wallet ? formatNaira(wallet.available_kobo / 100) : "—"} icon={WalletIcon} tone="success" />
            <KpiCard label="Total spent" value={formatNaira(totalSpentKobo / 100)} icon={TrendingUp} tone="muted" />
          </section>

          <section className="grid min-w-0 gap-4 lg:grid-cols-3">
            <DeliveriesCard title="Recent deliveries" parcels={deliveries.slice(0, 5)} viewAllTo="/app/deliveries" />
            <AlertsCard
              alerts={activity}
              onMarkRead={(id) =>
                setActivity((a) => a.map((n) => (n.id === id ? { ...n, read_at: new Date().toISOString() } : n)))
              }
            />
          </section>
        </>}
      </div>
    </>
  );
}
